import { normalizeStatus } from './thinkingStatus.js';
import { buildDisplayExecutionQueue, formatExecutionCardLine, formatStepStatus } from './thinkingLegacyProcessLines.js';
import { buildDocumentAgentOutputProcessLines } from './documentAgentOutputProcessLines.js';
import { buildWorkflowRunProcessEntries } from './workflowRunProcessLines.js';
import { workflowProgressProjection } from './workflowRunProjectionState.js';
import {
  SYSTEM_TRACE_TOKENS,
  formatExecutionDetail,
} from './thinkingProcessDetail.js';
import {
  buildNodeResultProcessEntry,
  resolveNodeResultProcessVisibility,
} from './nodeResultProcessLines.js';
import {
  formatResourceEntries,
  formatSourceLines,
} from './thinkingProcessActivityProjection.js';
const SOURCE_VISIBILITY_BY_PRESENCE = new Map([
  [true, 'visible'],
]);
const LEGACY_VISIBILITY_BY_NODE_RESULT_PRESENCE = new Map([
  [false, 'visible'],
]);

function normalizeText(value = '') {
  return String(value || '').trim();
}
function normalizeLower(value = '') {
  return normalizeText(value).toLowerCase();
}
function toLines(value = '') {
  return String(value || '')
    .split('\n')
    .map(normalizeText)
    .filter(Boolean);
}
function uniqueStrings(items = []) {
  return [...new Set(items.map(normalizeText).filter(Boolean))];
}
function uniqueProcessEntries(items = []) {
  return items
    .map((entry) => ({
      line: normalizeText(entry?.line),
      scope: normalizeText(entry?.scope),
      stepId: normalizeText(entry?.stepId),
    }))
    .filter(({ line }) => Boolean(line))
    .reduce((entries, entry) => (
      entries.some(({ line }) => line === entry.line) ? entries : [...entries, entry]
    ), []);
}

function resolveObject(value = null) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

const executionCardStepId = (trace) => normalizeText(trace.step_id || trace.node_result?.node_key);

function resolveExecutionCardEntry(trace = {}) {
  const nodeResultEntry = buildNodeResultProcessEntry(trace);
  const hasSources = formatSourceLines(trace.sources).length > 0;
  const hasNodeResult = Object.keys(resolveObject(trace.node_result)).length > 0;
  return {
    line: hasSources
      ? formatExecutionCardLine(trace)
      : nodeResultEntry.line || formatExecutionCardLine(trace),
    scope: nodeResultEntry.scope || (hasSources ? 'business' : ''),
    stepId: executionCardStepId(trace),
    visibility: SOURCE_VISIBILITY_BY_PRESENCE.get(hasSources)
      ?? LEGACY_VISIBILITY_BY_NODE_RESULT_PRESENCE.get(hasNodeResult)
      ?? resolveNodeResultProcessVisibility(trace),
  };
}

function buildExecutionCardEntries(executionCards = []) {
  return uniqueProcessEntries(
    buildDisplayExecutionQueue(executionCards)
      .map(resolveExecutionCardEntry)
      .filter(({ visibility }) => visibility === 'visible')
  );
}

function resolveKnowledgeSearchStatus(status = '', hasResults = false) {
  return normalizeStatus(status) || (hasResults ? 'completed' : 'running');
}

function buildKnowledgeSearchLines(item = {}) {
  const search = resolveObject(resolveObject(item).knowledgeSearch);
  const query = formatExecutionDetail(search.query);
  const results = Array.isArray(search.results) ? search.results : [];
  const sourceLines = formatSourceLines(results);
  const resultCount = Number(search.result_count);
  const hasCount = Number.isFinite(resultCount);
  const hasResults = [hasCount, sourceLines.length].some(Boolean);
  if (![query, hasCount, sourceLines.length].some(Boolean)) {
    return [];
  }
  const status = resolveKnowledgeSearchStatus(search.status, hasResults);
  const label = normalizeText(search.label) || '资料检索';
  return [[
    `[${formatStepStatus(status)}] ${label}`,
    query ? `查询 · ${query}` : '',
    sourceLines.length ? `读取 ${sourceLines.length} 份资料` : '',
    ...sourceLines,
    hasCount ? `结果 · 命中 ${resultCount} 条` : '',
  ].filter(Boolean).join('\n')];
}

function resolveExecutionCards(item = {}, hasKnowledgeSearch = false) {
  const cards = Array.isArray(item.executionCards) ? item.executionCards : [];
  return hasKnowledgeSearch
    ? cards.filter((trace) => !normalizeLower(trace?.step_id).includes('knowledge_search'))
    : cards;
}

function buildThinkingTraceLines(item = {}) {
  return toLines(item?.thinkingTrace)
    .filter((line) => !SYSTEM_TRACE_TOKENS.some((token) => normalizeLower(line).includes(token)));
}

function buildExecutionTraceLines(item = {}) {
  const executionTrace = resolveObject(item?.executionTrace);
  return [
    normalizeText(executionTrace.label || executionTrace.step_id),
    formatExecutionDetail(executionTrace.detail || executionTrace.reason),
  ];
}

function buildAgentStatusLines(item = {}) {
  const agentStatus = resolveObject(item?.agentStatus);
  return [formatExecutionDetail(agentStatus.detail || agentStatus.reason)];
}

const processDetailLines = (detail) => (Array.isArray(detail?.items) ? detail.items : [])
  .filter((item) => typeof item?.label === 'string' && typeof item.value === 'string')
  .map(({ label, value }) => `${label} · ${value}`);

const runtimeProcessEntries = (item) => item.processPhases
  .filter((activity) => activity?.activity_id && activity.label && activity.status)
  .map((activity) => ({
    line: [`[${activity.status}] ${activity.label}`, ...processDetailLines(activity.input_details),
      normalizeText(activity.summary), ...processDetailLines(activity.result_details)].filter(Boolean).join('\n'),
    scope: 'business', stepId: activity.activity_id, activityIds: [activity.activity_id],
  }));
export function resolveThinkingProcessEntries(item) {
  const normalizedItem = resolveObject(item);
  if (Array.isArray(normalizedItem.processPhases)) return runtimeProcessEntries(normalizedItem);
  const hasKnowledgeSearch = Object.keys(resolveObject(normalizedItem.knowledgeSearch)).length > 0;
  const executionCards = resolveExecutionCards(normalizedItem, hasKnowledgeSearch);
  const executionCardEntries = buildExecutionCardEntries(executionCards);
  const workflowStepEntries = buildWorkflowRunProcessEntries(normalizedItem);
  const outputEntries = buildDocumentAgentOutputProcessLines(
    resolveObject(normalizedItem.workflowProjection || normalizedItem.workflow_projection),
  ).map((line) => ({ line, scope: 'business' }));
  const evidenceEntries = uniqueProcessEntries([
    ...buildKnowledgeSearchLines(normalizedItem).map((line) => ({ line, scope: 'business' })),
    ...executionCardEntries,
  ]);
  if (workflowProgressProjection(normalizedItem) !== null || workflowStepEntries.length > 0) {
    return [...workflowStepEntries, ...uniqueProcessEntries(outputEntries)];
  }
  if (evidenceEntries.length > 0) {
    return evidenceEntries;
  }

  return uniqueStrings([
    ...buildThinkingTraceLines(normalizedItem),
    ...buildExecutionTraceLines(normalizedItem),
    ...buildAgentStatusLines(normalizedItem),
  ]).map((line) => ({ line, scope: '' }));
}

export function resolveThinkingProcessLines(item) {
  return resolveThinkingProcessEntries(item).map(({ line }) => line);
}

export function resolveThinkingProcessResourceEntries(item) {
  const normalizedItem = resolveObject(item);
  const search = resolveObject(normalizedItem.knowledgeSearch);
  const hasKnowledgeSearch = Object.keys(search).length > 0;
  const knowledgeLine = buildKnowledgeSearchLines(normalizedItem)[0] || '';
  const knowledgeEntry = {
    line: knowledgeLine,
    resources: formatResourceEntries(search.results),
  };
  const executionEntries = buildDisplayExecutionQueue(
    resolveExecutionCards(normalizedItem, hasKnowledgeSearch),
  ).map((trace) => {
    const entry = resolveExecutionCardEntry(trace);
    return {
      line: entry.visibility === 'visible' ? entry.line : '',
      resources: formatResourceEntries(trace.sources),
    };
  });
  return [knowledgeEntry, ...executionEntries]
    .filter(({ line, resources }) => Boolean(line && resources.length))
    .reduce((entries, entry) => (
      entries.some(({ line }) => line === entry.line) ? entries : [...entries, entry]
    ), []);
}
